import React, { useEffect, useMemo, useState } from "react";

const BASE_OPTIONS = [
    { value: "Currently pregnant", label: "Patient is currently pregnant" },
    { value: "Currently breastfeeding", label: "Patient is currently breastfeeding" },
    { value: "Currently trying to get pregnant", label: "Patient is trying to get pregnant" },
];

const BIRTH_CONTROL_ENTRY = {
    value: "Currently on birth control",
    label: "Patient is currently using birth control",
};

const BIRTH_CONTROL_OPTIONS = [
    { value: "Condom", label: "Condom" },
    { value: "Birth control pill", label: "Birth control pill" },
    { value: "IUD", label: "IUD" },
    { value: "Implant", label: "Implant" },
    { value: "Abstinence", label: "Abstinence" },
    { value: "Plan B", label: "Plan B" },
    { value: "Other", label: "Other" },
];

/**
 * Answer shape sent to onSubmit:
 * {
 *   selections: string[],           // empty array means "None of the above"
 *   birthControlMethods: string[],  // only populated when relevant
 *   otherBirthControl: string       // only populated when "Other" is chosen
 * }
 */
const MaternityQuestion = ({ onSubmit, onNext, initialValue, showBirthControl = false }) => {
    const [selections, setSelections] = useState([]);
    const [methods, setMethods] = useState([]);
    const [other, setOther] = useState("");
    const [error, setError] = useState("");

    const mainOptions = useMemo(
        () => (showBirthControl ? [...BASE_OPTIONS, BIRTH_CONTROL_ENTRY] : BASE_OPTIONS),
        [showBirthControl]
    );

    useEffect(() => {
        if (initialValue && typeof initialValue === "object") {
            setSelections(Array.isArray(initialValue.selections) ? initialValue.selections : []);
            setMethods(Array.isArray(initialValue.birthControlMethods) ? initialValue.birthControlMethods : []);
            setOther(initialValue.otherBirthControl || "");
        }
    }, [initialValue]);

    const toggleSelection = (value) => {
        setSelections((prev) => {
            let next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];

            // Mutual exclusivity: pregnant vs trying
            if (value === "Currently pregnant" || value === "Currently trying to get pregnant") {
                next = next.filter(
                    (v) =>
                        v !== (value === "Currently pregnant" ? "Currently trying to get pregnant" : "Currently pregnant")
                );
                // Clear BC if choosing either
                if (showBirthControl) {
                    next = next.filter((v) => v !== "Currently on birth control");
                    setMethods([]);
                    setOther("");
                }
            }

            // If choosing "Currently on birth control", allow only breastfeeding alongside
            if (value === "Currently on birth control" && showBirthControl) {
                const keep = ["Currently on birth control", "Currently breastfeeding"];
                next = next.filter((v) => keep.includes(v));
            }

            // Cap to two selections (e.g., breastfeeding + one other)
            if (next.length > 2) next = next.slice(1);

            setError("");
            return next;
        });
    };

    const toggleMethod = (m) => {
        setMethods((prev) => {
            const next = prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m];
            if (!next.includes("Other")) setOther("");
            return next;
        });
    };

    // Validation:
    // - If nothing selected → allowed (represents "None of the above")
    // - If "on birth control" chosen → must pick ≥1 method; if "Other" among them → must fill text
    const canSubmit = () => {
        if (selections.length === 0) return true; // "None of the above"
        const onBC = showBirthControl && selections.includes("Currently on birth control");
        if (onBC && methods.length === 0) return false;
        if (onBC && methods.includes("Other") && !other.trim()) return false;
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit()) {
            setError("Please complete the required selections.");
            return;
        }

        const onBC = showBirthControl && selections.includes("Currently on birth control");
        onSubmit({
            selections,                                 // [] means "None of the above"
            birthControlMethods: onBC ? methods : [],
            otherBirthControl: onBC && methods.includes("Other") ? other.trim() : "",
        });
        onNext();

        // reset
        setSelections([]);
        setMethods([]);
        setOther("");
        setError("");
    };

    return (
        <form onSubmit={handleSubmit} className="input-form">
            <div className="menstrual-cycle-options-container">
                {/* Main selections */}
                <div className="menstrual-cycle-options">
                    {mainOptions.map((opt) => {
                        const isSelected = selections.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={`menstrual-cycle-button ${isSelected ? "menstrual-cycle-button-selected" : ""}`}
                                onClick={() => toggleSelection(opt.value)}
                                aria-pressed={isSelected}
                            >
                                <div className="option-content">
                                    <div className="option-value">{opt.value}</div>
                                    <div className="option-description">{opt.label}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Birth control follow-ups */}
                {showBirthControl && selections.includes("Currently on birth control") && (
                    <div style={{ marginTop: 12 }}>
                        <div className="menstrual-cycle-options" style={{ rowGap: 8 }}>
                            {BIRTH_CONTROL_OPTIONS.map((m) => {
                                const isSelected = methods.includes(m.value);
                                return (
                                    <button
                                        key={m.value}
                                        type="button"
                                        className={`menstrual-cycle-button ${isSelected ? "menstrual-cycle-button-selected" : ""}`}
                                        onClick={() => toggleMethod(m.value)}
                                        aria-pressed={isSelected}
                                    >
                                        <div className="option-content">
                                            <div className="option-value">{m.value}</div>
                                            <div className="option-description">{m.label}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {methods.includes("Other") && (
                            <div style={{ marginTop: 8 }}>
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="Other birth control method"
                                    value={other}
                                    onChange={(e) => setOther(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                )}

                {error && <div className="error-message" style={{ marginTop: 8 }}>{error}</div>}

                {/* Bottom button: switches label based on selection presence */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                        type="submit"
                        className="treatment-submit-button"
                        disabled={!canSubmit()}
                    >
                        {selections.length > 0 ? "Continue" : "None of the above"}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default MaternityQuestion;
